import { assign, createMachine } from "xstate";
import { IOfferMode } from "@nju/data-access-portal-offers";
import {
  createOrchestrator,
  IFtthOrchestrator,
} from "@nju/data-access-portal-orchestrator";
import { createFtthOrder } from "@nju/data-access-portal-order";
import { loginWithActionToken, register } from "@nju/data-access-portal-user";
import { pushRegistrationEvent } from "@nju/data-layer";
import { BasicError } from "@nju/result";
import {
  hasAtLeastOneDigit,
  hasLowerAndUpperCase,
  hasSpecialChar,
  isValidEmail,
} from "@nju/utils";
import { generalErrorMessage } from "@nju/utils";

interface IContext {
  email: string;
  password: string;
  emailError?: string;
  passwordError?: string;
  formError?: string;
  offerIds?: string[];
  mode: IOfferMode;
  numberOfRetries: number;
}

type IEvent =
  | {
      type: "SET_INPUT_VALUE";
      name: "email" | "password";
      value: string;
    }
  | { type: "SUBMIT" }
  | { type: "GENERAL_ERROR" }
  | { type: "USER_ALREADY_EXISTS" }
  | {
      type: "USER_CREATED";
    }
  | { type: "CREATE_ORCHESTRATOR_ERROR" }
  | {
      type: "REGISTRATION_SUCCESSFUL";
      orchestrator?: IFtthOrchestrator;
    };

export const registrationMachine = createMachine<IContext, IEvent>(
  {
    context: {
      email: "",
      password: "",
      mode: "ftth",
      numberOfRetries: 0,
    },
    schema: {
      context: {} as IContext,
      events: {} as IEvent,
    },
    initial: "ready",
    states: {
      ready: {
        on: {
          SET_INPUT_VALUE: {
            actions: "setInputValue",
          },
          SUBMIT: {
            target: "validate",
          },
        },
      },
      validate: {
        entry: "validateForm",
        always: [
          {
            cond: "hasNoInputErrors",
            target: "registerWithForm",
          },
          {
            target: "ready",
          },
        ],
      },
      registerWithForm: {
        tags: ["loading"],
        invoke: {
          src: "registerWithForm",
        },
        on: {
          GENERAL_ERROR: {
            actions: "setFormError",
            target: "ready",
          },
          USER_ALREADY_EXISTS: {
            actions: "setFormError",
            target: "ready",
          },
          USER_CREATED: {
            target: "finishRegistration",
          },
        },
      },
      finishRegistration: {
        tags: ["loading"],
        invoke: {
          src: "finishRegistration",
        },
        on: {
          REGISTRATION_SUCCESSFUL: {
            actions: "onSubmit",
          },
          GENERAL_ERROR: {
            actions: "setFormError",
            target: "ready",
          },
          CREATE_ORCHESTRATOR_ERROR: {
            actions: "setNumberOfRetries",
            target: "idleRegistration",
          },
        },
      },
      idleRegistration: {
        tags: ["loading"],
        after: [
          {
            delay: 2000,
            target: "finishRegistration",
          },
        ],
      },
    },
  },
  {
    services: {
      registerWithForm: ({ email, password }) => {
        return async (callback) => {
          const result = await register({ email: email.trim(), password });

          if (result.isError) {
            if (result.err.code === "USER_ALREADY_EXISTS") {
              return callback({ type: "USER_ALREADY_EXISTS" });
            }

            return callback({ type: "GENERAL_ERROR" });
          }

          try {
            (
              await loginWithActionToken({
                actionToken: result.value.actionToken,
              })
            ).unwrap();
          } catch {
            return callback({ type: "GENERAL_ERROR" });
          }

          pushRegistrationEvent({ method: "credentials" });
          return callback({
            type: "USER_CREATED",
          });
        };
      },
      finishRegistration: ({ offerIds, mode, numberOfRetries }) => {
        return async (callback) => {
          // registration just on /zaloz-konto without any query params
          if (!mode) {
            return callback({
              type: "REGISTRATION_SUCCESSFUL",
            });
          }

          if (["mobile", "postpaid"].includes(mode)) {
            return callback({
              type: "REGISTRATION_SUCCESSFUL",
            });
          }

          if (mode === "ftth") {
            const result = await createOrchestrator("NJU_FTTH_ONBOARDING", {});

            if (result.isError) {
              if (
                result.err.code === "422_UNPROCESSABLE_CONTENT" &&
                numberOfRetries < 8
              ) {
                return callback({ type: "CREATE_ORCHESTRATOR_ERROR" });
              }
              return callback({ type: "GENERAL_ERROR" });
            }

            const orchestrator = result.unwrap();

            if (!orchestrator) {
              throw new BasicError({
                message: "Missing orchestrator",
              });
            }

            const { id: orchestratorId } = orchestrator;
            const installationToken =
              window.sessionStorage.getItem("installationToken");

            if (installationToken) {
              if (!Array.isArray(offerIds)) {
                return callback({ type: "GENERAL_ERROR" });
              }

              const orchestratorWithOrder = (
                await createFtthOrder({
                  mode: "ftth",
                  orchestratorId,
                  installationToken,
                  offerIds,
                })
              ).unwrap();

              return callback({
                type: "REGISTRATION_SUCCESSFUL",
                orchestrator: orchestratorWithOrder as IFtthOrchestrator,
              });
            }

            return callback({
              type: "REGISTRATION_SUCCESSFUL",
              orchestrator,
            });
          }
        };
      },
    },
    actions: {
      setNumberOfRetries: assign<IContext, IEvent>((context, event) => {
        if (event.type === "CREATE_ORCHESTRATOR_ERROR") {
          return {
            numberOfRetries: context.numberOfRetries + 1,
          };
        }

        return context;
      }),
      setFormError: assign<IContext, IEvent>((context, event) => {
        if (event.type === "USER_ALREADY_EXISTS") {
          return {
            emailError: "Istnieje już konto założone na ten e-mail.",
            formError: undefined,
          };
        }

        if (event.type === "GENERAL_ERROR") {
          return {
            formError: generalErrorMessage,
          };
        }

        return context;
      }),
      validateForm: assign<IContext, IEvent>((context, _) => {
        let emailError: string | undefined;
        let passwordError: string | undefined;

        if (!context.email.trim()) {
          emailError = "Podaj adres e-mail.";
        }

        if (!isValidEmail(context.email.trim())) {
          emailError = "Podaj poprawny adres e-mail.";
        }

        if (!context.password.trim()) {
          passwordError = "Wpisz hasło, które spełnia kryteria.";
        }

        if (
          !hasAtLeastOneDigit(context.password) ||
          !hasSpecialChar(context.password) ||
          !hasLowerAndUpperCase(context.password) ||
          context.password.length < 8
        ) {
          passwordError = "Wpisz hasło, które spełnia kryteria.";
        }

        return { formError: undefined, passwordError, emailError };
      }),
      setInputValue: assign<IContext, IEvent>((context, event) => {
        if (event.type === "SET_INPUT_VALUE") {
          return {
            [event.name]: event.value,
          };
        }

        return context;
      }),
    },
    guards: {
      hasNoInputErrors: ({ emailError, passwordError }) => {
        return emailError === undefined && passwordError === undefined;
      },
    },
  },
);
